import Swal from "sweetalert2";
import { t } from "i18next";
import { const_routes } from "../helpers/const_routes";
import { fetchToken } from "../helpers/fetch";
import { types } from "../types/types";
import { startLogout } from "./authActions";

const handleError = (body, dispatch) => {
  if (body.code === "bak-error-001") {
    Swal.fire(t("titles.error"), `${t("alerts.validation.bak-error-001")} - ${body.method}`, "error");
  }
  if (body.code === "bak-aut-tok-inv-001" || body.code === "bak-aut-tok-inv-002") {
    Swal.fire(t("titles.error"), t("api.token.invalid_petition"), "error");
    dispatch(startLogout());
  }
  if (body.ok === false && body.msg) {
    Swal.fire(t("titles.error"), body.msg, "error");
  }
};

export const listFlowCoverageSnapshot = async (filters = {}) => {
  const query = new URLSearchParams(
    Object.entries(filters).filter(([, value]) => value !== "" && value !== null && value !== undefined)
  ).toString();
  const resp = await fetchToken(
    const_routes.SCHEMA,
    const_routes.VERSIONV1,
    const_routes.ROUTE_FLOW_COVERAGE,
    query ? `get?${query}` : "get"
  );
  const body = await resp.json();
  if (body.ok) {
    return body;
  }
  return null;
};

export const listMyFlowCoverageGroups = async (filters = {}) => {
  const query = new URLSearchParams(
    Object.entries(filters).filter(([, value]) => value !== "" && value !== null && value !== undefined)
  ).toString();
  const resp = await fetchToken(
    const_routes.SCHEMA,
    const_routes.VERSIONV1,
    const_routes.ROUTE_FLOW_COVERAGE,
    query ? `mine?${query}` : "mine"
  );
  const body = await resp.json();
  if (body.ok) {
    return body.flowCoverageGroups || [];
  }
  return [];
};

export const startLoadingFlowCoverage = (filters = {}, options = {}) => {
  return async (dispatch) => {
    if (!options.silent) {
      dispatch({ type: types.FLOW_COVERAGE_SET_LOADING, payload: { loading: true, reset: true } });
    }
    try {
      const query = new URLSearchParams(
        Object.entries(filters).filter(([, value]) => value !== "" && value !== null && value !== undefined)
      ).toString();
      const resp = await fetchToken(
        const_routes.SCHEMA,
        const_routes.VERSIONV1,
        const_routes.ROUTE_FLOW_COVERAGE,
        query ? `get?${query}` : "get"
      );
      const body = await resp.json();
      if (body.ok) {
        dispatch({ type: types.FLOW_COVERAGE_SET, payload: body });
      } else {
        handleError(body, dispatch);
      }
    } finally {
      dispatch({ type: types.FLOW_COVERAGE_SET_LOADING, payload: false });
    }
  };
};

export const startLoadingFlowCoverageGroups = (filters = {}) => {
  return async (dispatch) => {
    dispatch({ type: types.FLOW_COVERAGE_SET_LOADING, payload: true });
    try {
      const query = new URLSearchParams(
        Object.entries(filters).filter(([, value]) => value !== "" && value !== null && value !== undefined)
      ).toString();
      const resp = await fetchToken(
        const_routes.SCHEMA,
        const_routes.VERSIONV1,
        const_routes.ROUTE_FLOW_COVERAGE,
        query ? `groups?${query}` : "groups"
      );
      const body = await resp.json();
      if (body.ok) {
        dispatch({ type: types.FLOW_COVERAGE_SET, payload: body });
      } else {
        handleError(body, dispatch);
      }
    } finally {
      dispatch({ type: types.FLOW_COVERAGE_SET_LOADING, payload: false });
    }
  };
};

export const startSaveFlowCoverageGroup = (data, id = null) => {
  return async (dispatch) => {
    const suffix = id ? `group/${id}` : "group";
    const method = id ? "PUT" : "POST";
    const resp = await fetchToken(
      const_routes.SCHEMA,
      const_routes.VERSIONV1,
      const_routes.ROUTE_FLOW_COVERAGE,
      suffix,
      data,
      method
    );
    const body = await resp.json();
    if (body.ok) {
      return body.flowCoverageGroup;
    }
    handleError(body, dispatch);
    return null;
  };
};

export const startDeleteFlowCoverageGroup = (id) => {
  return async (dispatch) => {
    const resp = await fetchToken(
      const_routes.SCHEMA,
      const_routes.VERSIONV1,
      const_routes.ROUTE_FLOW_COVERAGE,
      `group/${id}`,
      {},
      "DELETE"
    );
    const body = await resp.json();
    if (body.ok) {
      return true;
    }
    handleError(body, dispatch);
    return false;
  };
};

export const startAssignFlowCoverageMember = (data) => {
  return async (dispatch) => {
    const resp = await fetchToken(
      const_routes.SCHEMA,
      const_routes.VERSIONV1,
      const_routes.ROUTE_FLOW_COVERAGE,
      "assign",
      data,
      "POST"
    );
    const body = await resp.json();
    if (body.ok) {
      if (body.alreadyMember) {
        Swal.fire(
          t("titles.info", t("titles.attention", "Aviso")),
          body.msg || t("admin.flowModule.flowCoverage.alreadyInGroup"),
          "info"
        );
      }
      return body.flowCoverageMember;
    }
    handleError(body, dispatch);
    return null;
  };
};

export const startUpdateFlowCoverageMember = (id, data) => {
  return async (dispatch, getState) => {
    const memberId = Number(id);
    const groups = getState().flowCoverage?.groups || [];
    let previousActive;
    groups.forEach((group) => {
      (group.FlowCoverageMember_To_FlowCoverageGroup || []).forEach((member) => {
        if (Number(member.id) === memberId) {
          previousActive = member.active;
        }
      });
    });
    dispatch({
      type: types.FLOW_COVERAGE_PATCH_MEMBER,
      payload: { id: memberId, changes: data }
    });
    const resp = await fetchToken(
      const_routes.SCHEMA,
      const_routes.VERSIONV1,
      const_routes.ROUTE_FLOW_COVERAGE,
      `member/${id}`,
      data,
      "PUT"
    );
    const body = await resp.json();
    if (body.ok) {
      return body.flowCoverageMember;
    }
    if (previousActive !== undefined) {
      dispatch({
        type: types.FLOW_COVERAGE_PATCH_MEMBER,
        payload: { id: memberId, changes: { active: previousActive } }
      });
    }
    handleError(body, dispatch);
    return null;
  };
};

export const startDeleteFlowCoverageMember = (id) => {
  return async (dispatch) => {
    const resp = await fetchToken(
      const_routes.SCHEMA,
      const_routes.VERSIONV1,
      const_routes.ROUTE_FLOW_COVERAGE,
      `member/${id}`,
      {},
      "DELETE"
    );
    const body = await resp.json();
    if (body.ok) {
      return true;
    }
    handleError(body, dispatch);
    return false;
  };
};

export const startReorderFlowCoverageMembers = (groupId, orderedIds) => {
  return async (dispatch) => {
    const resp = await fetchToken(
      const_routes.SCHEMA,
      const_routes.VERSIONV1,
      const_routes.ROUTE_FLOW_COVERAGE,
      `reorder/${groupId}`,
      { ordered_ids: orderedIds },
      "PUT"
    );
    const body = await resp.json();
    if (body.ok) {
      return true;
    }
    handleError(body, dispatch);
    return false;
  };
};
